import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAccountData } from '../../redux/main/accountSlice';
import { selectUser } from '../../redux/main/authSlice';

import CustomerSelect from '../../components/CustomerSelect';
import { apiService } from '../../utils/apiService';
import { toast } from 'react-toastify';
import { formatDurationMinutes, formatDistance } from '../../utils/formatUtils.js';
import AssistanceTypeServices, { sortAssistanceTypes } from '../../components/assistanceRequestsPage/AssistanceTypeServices';

// Componenti per i vari step del wizard
const ClienteStep = ({ formData, setFormData, nextStep }) => {
  const token = localStorage.getItem('installamelo-auth-token');
  const accountData = useSelector(selectAccountData);

  // Inizializzare gli stati con i valori esistenti in formData, se presenti
  const [selectedCustomer, setSelectedCustomer] = useState(() => {
    return formData.cliente && Object.keys(formData.cliente).length > 0 
      ? { 
          customerId: formData.cliente.id, 
          customerLabel: formData.cliente.nome || formData.cliente.label, 
          value: formData.cliente.id || formData.cliente.value,
          label: formData.cliente.nome || formData.cliente.label,
          data: formData.cliente.data || formData.cliente 
        }
      : null;
  });
  
  // Flag per il primo caricamento in modalità edit
  const initialEditLoadRef = useRef(true);
  
  // Quando formData cambia, aggiorna anche lo stato locale (solo per il caricamento iniziale in modalità edit)
  useEffect(() => {
    if (formData.isEditMode && formData.cliente && 
        Object.keys(formData.cliente).length > 0 && 
        initialEditLoadRef.current) {
      
      console.log('Aggiornamento iniziale selectedCustomer da formData:', formData.cliente);
      
      setSelectedCustomer({ 
        customerId: formData.cliente.id, 
        customerLabel: formData.cliente.nome || formData.cliente.label, 
        value: formData.cliente.id || formData.cliente.value,
        label: formData.cliente.nome || formData.cliente.label,
        data: formData.cliente.data || formData.cliente 
      });
      
      prevCustomerRef.current = formData.cliente.id;
      
      initialEditLoadRef.current = false;
    }
  }, [formData.cliente, formData.isEditMode]);

  const prevCustomerRef = useRef(formData.cliente?.id || null);
  
  useEffect(() => {
    if (!selectedCustomer) return;
    
    if (formData.isEditMode && initialEditLoadRef.current) return;
    
    const isCustomerChanged = prevCustomerRef.current !== null && 
                             prevCustomerRef.current !== selectedCustomer.customerId;
    
    console.log('Verifica cambio cliente:', { 
      prev: prevCustomerRef.current, 
      current: selectedCustomer.customerId,
      isChanged: isCustomerChanged,
      isEditMode: formData.isEditMode,
      isFirstLoad: initialEditLoadRef.current
    });
    
    if (isCustomerChanged) {
      console.log('Cliente cambiato da', prevCustomerRef.current, 'a', selectedCustomer.customerId);
      setFormData({
        isEditMode: formData.isEditMode,
        cliente: {
          id: selectedCustomer.customerId,
          nome: selectedCustomer.customerLabel,
          ...selectedCustomer.data
        }
      });
      toast.info('Cliente cambiato. Tutti i dati della richiesta sono stati reimpostati.');
    } else if (!formData.isEditMode || !initialEditLoadRef.current) {
      setFormData(prev => ({
        ...prev,
        cliente: {
          id: selectedCustomer.customerId,
          nome: selectedCustomer.customerLabel,
          ...selectedCustomer.data
        }
      }));
    }
    
    prevCustomerRef.current = selectedCustomer.customerId;
  }, [selectedCustomer, formData.isEditMode, setFormData]);

  return (
    <div className={isMobile ? "p-0" : "p-6"}>
      <h2 className={`font-semibold ${isMobile ? 'text-base mb-3' : 'text-xl mb-6'}`}>Seleziona il Cliente</h2>
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Cerca e seleziona un cliente
        </label>
        <CustomerSelect 
          selectedOption={selectedCustomer} 
          setSelectedOption={setSelectedCustomer}
          placeholder="Cerca cliente per nome, codice fiscale o partita IVA"
        />
        
        {selectedCustomer && (
          <div className="mt-6 p-4 bg-blue-50 rounded-md border border-blue-200">
            <div className="flex items-center mb-3">
              <i className="fa-duotone fa-user text-blue-500 mr-2"></i>
              <h3 className="text-lg font-medium">Cliente selezionato</h3>
            </div>
            
            <p className="font-medium text-lg">{selectedCustomer.label}</p>
            
            {selectedCustomer.data?.vatNumber && (
              <p className="text-sm text-gray-600 mt-1">
                <strong>P.IVA:</strong> {selectedCustomer.data.vatNumber}
              </p>
            )}
            
            {selectedCustomer.data?.addresses && selectedCustomer.data.addresses.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  <strong>Indirizzo principale:</strong> {selectedCustomer.data.addresses[0].addressLabel}
                </p>
              </div>
            )}
            
            {selectedCustomer.data?.contactsList && selectedCustomer.data.contactsList.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  <strong>Contatto:</strong> {selectedCustomer.data.contactsList[0].name} {selectedCustomer.data.contactsList[0].surname}
                  {selectedCustomer.data.contactsList[0].mobile && ` - ${selectedCustomer.data.contactsList[0].mobile}`}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="flex justify-end">
        <button 
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center"
          onClick={nextStep}
          disabled={!selectedCustomer}
        >
          Avanti
          <i className="fa-solid fa-chevron-right ml-2"></i>
        </button>
      </div>
    </div>
  );
};

const ScegliImpiantoStep = ({ formData, setFormData, nextStep, prevStep, comeFromStep3, isMobile }) => {
  const [segnalazione, setSegnalazione] = useState(formData.segnalazione || '');
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(formData.sistema || null);
  const [selectedSystemType, setSelectedSystemType] = useState(formData.tipoSistema || null);
  const [selectedUseCases, setSelectedUseCases] = useState(formData.useCases || []);
  const timerRef = useRef(null);
  const accountData = useSelector(selectAccountData);
  
  // Fetch customer systems when cliente is selected or component mounts
  useEffect(() => {
    if (formData.cliente && formData.cliente.id) {
      console.log('Cliente selezionato, carico gli impianti:', formData.cliente);
      fetchCustomerSystems();
    }
  }, [formData.cliente]); // La dipendenza è formData.cliente, quindi quando viene precaricato dal componente padre, questo useEffect si attiverà
  
  // Fetch customer systems
  const fetchCustomerSystems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('installamelo-auth-token');
      
      // Ottieni l'accountId dalla variabile accountData o dal localStorage
      let accountId = accountData?._id;
      if (!accountId) {
        const userData = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
        accountId = userData?.accountId;
      }
      
      // Ottieni il customerId da formData.cliente.id
      const customerId = formData.cliente?.id;
      
      if (!accountId || !customerId) {
        console.error('accountId o customerId mancanti:', { accountId, customerId });
        throw new Error('Dati mancanti per il caricamento degli impianti');
      }
      
      console.log('Caricamento impianti per:', { accountId, customerId });
      const url = `${import.meta.env.VITE_API_BASE_URL}/systems/systemsList?accountId=${accountId}&customerId=${customerId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Errore nel caricamento degli impianti');
      }
      
      const data = await response.json();
      console.log('Impianti caricati:', Array.isArray(data) ? data.length : 'oggetto diretto', data);
      
      // Verifica se data è un array o un oggetto
      const systemsArray = Array.isArray(data) ? data : [data];
      
      if (systemsArray.length > 0) {
        setSystems(systemsArray);
        
        // Se c'è un solo impianto, selezionalo automaticamente
        if (systemsArray.length === 1) {
          console.log('Impianto unico selezionato automaticamente:', systemsArray[0]);
          setSelectedSystem(systemsArray[0]);
          
          // Se l'impianto ha un solo tipo, selezionalo automaticamente
          if (systemsArray[0].typesList && systemsArray[0].typesList.length === 1) {
            console.log('Tipo impianto unico selezionato automaticamente:', systemsArray[0].typesList[0]);
            setSelectedSystemType(systemsArray[0].typesList[0]);
          }
        }
      } else {
        console.log('Nessun impianto trovato per questo cliente');
      }
    } catch (error) {
      console.error('Errore nel caricamento degli impianti:', error);
      toast.error('Errore nel caricamento degli impianti: ' + (error.message || 'Errore sconosciuto'));
    } finally {
      setLoading(false);
    }
  };
  
  // Check if warranty is expired
  const isWarrantyExpired = (warrantyExpirationDate) => {
    if (!warrantyExpirationDate) return true;
    const currentDate = new Date();
    const expirationDate = new Date(warrantyExpirationDate);
    return currentDate > expirationDate;
  };
  
  // Format date to DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getRelevanceColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Handle system selection
  const handleSystemSelect = (system) => {
    // Make sure we have a valid system with an ID
    if (!system || !system._id) {
      console.error('Sistema non valido o senza ID', system);
      return;
    }

    // Check if we're deselecting the current system
    if (selectedSystem && selectedSystem._id === system._id) {
      setSelectedSystem(null);
      setSelectedSystemType(null);
      setFormData(prev => ({
        ...prev,
        sistema: null,
        tipoSistema: null
      }));
      return;
    }
    
    // Select the new system
    setSelectedSystem(system);
    setSelectedSystemType(null);
    setFormData(prev => ({
      ...prev,
      sistema: system,
      tipoSistema: null
    }));
  };

  const loadUseCases = async () => {
    try {
      const response = await apiService.get('/api/use-cases');
      return response.data;
    } catch (error) {
      console.error('Errore nel caricamento degli use cases:', error);
      toast.error('Errore nel caricamento degli use cases');
      return [];
    }
  };

  const fetchUseCases = async () => {
    const useCases = await loadUseCases();
    return useCases;
  };

  const analyzeWithAI = async (assistanceRequestUseCases) => {
    try {
      const response = await apiService.post('/api/assistance-requests/analyze', {
        text: segnalazione,
        useCases: assistanceRequestUseCases
      });
      return response.data;
    } catch (error) {
      console.error('Errore nell\'analisi AI:', error);
      toast.error('Errore nell\'analisi AI');
      return null;
    }
  };

  const handleAnalysisSystemSelect = (impianto) => {
    handleSystemSelect(impianto, false);
  };

  // Handle system type selection
  const handleSystemTypeSelect = (tipo) => {
    // Make sure we have a valid type with an ID
    if (!tipo || !tipo._id) {
      console.error('Tipo impianto non valido o senza ID', tipo);
      return;
    }

    // Check if we're deselecting the current type
    if (selectedSystemType && selectedSystemType._id === tipo._id) {
      setSelectedSystemType(null);
      setFormData(prev => ({
        ...prev,
        tipoSistema: null
      }));
      return;
    }
    
    // Select the new system type
    setSelectedSystemType(tipo);
    setFormData(prev => ({
      ...prev,
      tipoSistema: tipo
    }));
  };

  const handleUseCaseSelect = (useCase) => {
    if (formData.useCase && formData.useCase.id === useCase.id) {
      setFormData(prev => ({
        ...prev,
        useCase: null
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      useCase: {
        id: useCase.id,
        nome: useCase.nome,
        ...useCase
      }
    }));
  };

  const handleGenericRequestSelect = () => {
    setFormData(prev => ({
      ...prev,
      useCase: {
        id: 'generic',
        nome: 'Richiesta generica'
      }
    }));
  };

  const startElapsedTimer = () => {
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  };

  const stopElapsedTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const analyzeText = () => {
    if (!segnalazione.trim()) {
      toast.error('Inserisci una descrizione del problema');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      segnalazione
    }));
    
    nextStep();
  };

  const handleTextChange = (e) => {
    setSegnalazione(e.target.value);
  };

  const handleMicClick = async () => {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks = [];
        
        recorder.ondataavailable = (e) => {
          chunks.push(e.data);
        };
        
        recorder.onstop = async () => {
          const audioBlob = new Blob(chunks, { type: 'audio/wav' });
          const wavBlob = await convertToWav(audioBlob);
          
          try {
            const formData = new FormData();
            formData.append('audio', wavBlob, 'recording.wav');
            
            const response = await apiService.post('/api/assistance-requests/transcribe', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            
            setSegnalazione(prev => prev + ' ' + response.data.text);
          } catch (error) {
            console.error('Errore nella trascrizione:', error);
            toast.error('Errore nella trascrizione audio');
          }
        };
        
        setMediaRecorder(recorder);
        setAudioChunks(chunks);
        recorder.start();
        setIsRecording(true);
        setElapsedTime(0);
        startElapsedTimer();
      } catch (error) {
        console.error('Errore nell\'accesso al microfono:', error);
        toast.error('Errore nell\'accesso al microfono');
      }
    } else {
      mediaRecorder.stop();
      setIsRecording(false);
      stopElapsedTimer();
    }
  };

  // Handle use case selection
  const handleUseCaseToggle = (useCase) => {
    setSelectedUseCases(prev => {
      // Check if the use case is already selected
      const isSelected = prev.some(uc => uc._id === useCase._id);
      
      // If selected, remove it, otherwise add it
      return isSelected
        ? prev.filter(uc => uc._id !== useCase._id)
        : [...prev, useCase];
    });
  };
  
  // Update form data when selectedUseCases changes
  useEffect(() => {
    setFormData(prevData => ({
      ...prevData,
      useCases: selectedUseCases
    }));
  }, [selectedUseCases, setFormData]);
  
  const handleNext = () => {
    // Validazione impianto selezionato se ci sono impianti
    if (systems.length > 0 && !selectedSystem) {
      toast.error('Seleziona un impianto per proseguire');
      return;
    }
    
    // Se l'impianto ha tipi e non è stata selezionata l'opzione generica
    if (selectedSystem && selectedSystem.typesList && 
        selectedSystem.typesList.length > 0 && !selectedSystemType) {
      toast.error('Seleziona un tipo di impianto per proseguire');
      return;
    }
    
    // Aggiorna il formData con tutti i dati necessari
    setFormData(prev => ({
      ...prev,
      // Salva i dati dell'impianto selezionato
      sistema: selectedSystem ? {
        _id: selectedSystem._id,
        title: selectedSystem.title || selectedSystem.nome,
        nome: selectedSystem.title || selectedSystem.nome,
        customerAddressId: selectedSystem.customerAddressId,
        customerAddressLabel: selectedSystem.customerAddressLabel,
        geoData: selectedSystem.geoData
      } : null,
      // Salva i dati del tipo di impianto selezionato
      tipoSistema: selectedSystemType ? {
        _id: selectedSystemType._id,
        typeLabel: selectedSystemType.typeLabel,
        expirationWarrantyDate: selectedSystemType.expirationWarrantyDate
      } : null
    }));
    
    console.log('Impianto selezionato:', {
      sistema: selectedSystem,
      tipoSistema: selectedSystemType
    });
    
    nextStep();
  };

  return (
    <div className={isMobile ? "p-0" : "p-6"}>
      <h2 className={`font-semibold ${isMobile ? 'text-base mb-3' : 'text-xl mb-6'}`}>Scegli l'Impianto</h2>
      
      <div className={isMobile ? "mb-2" : "mb-4"}>
        <p className={`text-gray-600 ${isMobile ? 'text-sm mb-2' : 'mb-2'}`}>
          Seleziona l'impianto per cui stai richiedendo assistenza. Tutti gli impianti registrati a tuo nome sono elencati qui sotto.
        </p>
      </div>
      
      {/* Lista impianti del cliente */}
      <div className={isMobile ? "mb-4" : "mb-8"}>
        <h3 className={`font-medium ${isMobile ? 'text-sm mb-2' : 'text-lg mb-4'}`}>Impianti del cliente</h3>
        
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : systems.length === 0 ? (
          <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
            <p>Nessun impianto trovato per questo cliente.</p>
          </div>
        ) : (
          <div className={isMobile ? "space-y-3" : "space-y-6"}>
            {systems.map(system => (
              <div 
                key={system._id}
                className={`${isMobile ? 'p-3' : 'p-4'} border rounded-lg transition-colors ${selectedSystem && selectedSystem._id === system._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
              >
                <div className="flex items-start">
                  <div 
                    className={`mt-1 ${isMobile ? 'w-4 h-4' : 'w-5 h-5'} rounded-full border-2 flex-shrink-0 cursor-pointer ${selectedSystem && selectedSystem._id === system._id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}
                    onClick={() => handleSystemSelect(system)}
                  ></div>
                  
                  <div className={`${isMobile ? 'ml-2' : 'ml-3'} flex-grow`}>
                    <h4 className={`font-medium ${isMobile ? 'text-sm' : 'text-lg'} cursor-pointer`} onClick={() => handleSystemSelect(system)}>{system.title || system.nome}</h4>
                    <div className={`${isMobile ? 'mt-0.5 text-xs' : 'mt-1 text-sm'}`}>
                      {system.customerAddressLabel && (
                        <p className="text-gray-600">
                          <i className={`fa-solid fa-location-dot mr-1 text-gray-400 ${isMobile ? 'text-xs' : ''}`}></i>
                          {system.customerAddressLabel}
                        </p>
                      )}
                      
                      {/* Rimosse informazioni di distanza e tempo su richiesta dell'utente */}
                    </div>
                    
                    {/* Lista tipi di impianto */}
                    {selectedSystem && selectedSystem._id === system._id && system.typesList && system.typesList.length > 0 && (
                      <div className="mt-4 bg-white p-3 rounded-md border border-gray-100">
                        <p className="font-medium mb-2">Tipi di impianto:</p>
                        <div className="space-y-3">
                          {system.typesList.map(tipo => (
                            <div 
                              key={tipo._id}
                              className={`p-3 border rounded ${selectedSystemType && selectedSystemType._id === tipo._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                            >
                              <div className="flex items-start">
                                <div 
                                  className={`mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 cursor-pointer ${selectedSystemType && selectedSystemType._id === tipo._id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}
                                  onClick={() => handleSystemTypeSelect(tipo)}
                                ></div>
                                
                                <div className="ml-3 w-full">
                                  <p className="font-medium cursor-pointer" onClick={() => handleSystemTypeSelect(tipo)}>{tipo.typeLabel}</p>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-2 text-sm mt-2">
                                    {tipo.testedDate && (
                                      <p className="mb-1"><span className="text-gray-600">Data collaudo:</span> {formatDate(tipo.testedDate)}</p>
                                    )}
                                    
                                    {tipo.expirationWarrantyDate && (
                                      <div className="flex items-center mb-1">
                                        <span className="text-gray-600 mr-1">Garanzia:</span>
                                        <span className={new Date() > new Date(tipo.expirationWarrantyDate) ? 'text-red-500' : 'text-green-500 font-medium'}>
                                          {new Date() > new Date(tipo.expirationWarrantyDate) ? 
                                            'Fuori garanzia' : 
                                            'In garanzia'}
                                        </span>
                                        <span className="text-xs text-gray-500 ml-1">
                                          {new Date() > new Date(tipo.expirationWarrantyDate) ? 
                                            `(Scaduta il ${formatDate(tipo.expirationWarrantyDate)})` : 
                                            `(Scade il ${formatDate(tipo.expirationWarrantyDate)})`
                                          }
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {!isMobile && (
        <div className="flex justify-between">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors flex items-center"
            onClick={prevStep}
          >
            <i className="fa-solid fa-chevron-left mr-2"></i>
            Indietro
          </button>
          
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center"
            onClick={handleNext}
          >
            Avanti
            <i className="fa-solid fa-chevron-right ml-2"></i>
          </button>
        </div>
      )}
    </div>
  );
};

const SegnalazioneStep = ({ formData, setFormData, nextStep, prevStep, isMobile }) => {
  const [segnalazione, setSegnalazione] = useState(formData.segnalazione || '');
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showError, setShowError] = useState(false);
  const timerRef = useRef(null);
  
  const startElapsedTimer = () => {
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  };

  const stopElapsedTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };
  
  // Assicurati di pulire il timer quando il componente viene smontato
  useEffect(() => {
    return () => {
      stopElapsedTimer();
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
    };
  }, [mediaRecorder]);

  // Event listener per errore triggerato dalla navigazione mobile
  useEffect(() => {
    const handleShowError = () => {
      setShowError(true);
      setTimeout(() => {
        const errorElement = document.getElementById('segnalazione-error');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    };

    window.addEventListener('showSegnalazioneError', handleShowError);
    return () => {
      window.removeEventListener('showSegnalazioneError', handleShowError);
    };
  }, []);

  const handleMicClick = async () => {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks = [];
        
        recorder.ondataavailable = (e) => {
          chunks.push(e.data);
        };
        
        recorder.onstop = () => {
          setIsRecording(false);
          stopElapsedTimer();
          
          // Creare un blob audio e convertirlo in testo
          // Per ora lo lascio vuoto, ma puoi implementare la conversione audio-testo
        };
        
        setAudioChunks([]);
        setMediaRecorder(recorder);
        recorder.start();
        setIsRecording(true);
        setElapsedTime(0);
        startElapsedTimer();
      } catch (error) {
        console.error('Errore nell\'accesso al microfono:', error);
        toast.error('Impossibile accedere al microfono');
      }
    } else if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
      stopElapsedTimer();
    }
  };

  const handleTextChange = (e) => {
    setSegnalazione(e.target.value);
    if (showError && e.target.value.trim()) {
      setShowError(false);
    }
  };

  const handleNext = () => {
    if (!segnalazione.trim()) {
      setShowError(true);
      // Scroll al campo per rendere visibile l'errore
      setTimeout(() => {
        const errorElement = document.getElementById('segnalazione-error');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    setShowError(false);
    setFormData(prev => ({
      ...prev,
      segnalazione
    }));
    
    nextStep();
  };

  return (
    <div className={isMobile ? "p-0" : "p-6"}>
      <h2 className={`font-semibold ${isMobile ? 'text-base mb-3' : 'text-xl mb-6'}`}>Descrizione del Problema</h2>
      
      <div className={isMobile ? "mb-4" : "mb-8"}>
        <label className={`block font-medium text-gray-700 ${isMobile ? 'text-xs mb-1.5' : 'text-sm mb-2'}`}>
          Descrivi il problema riscontrato <span className="text-red-500">*</span>
        </label>
        
        <div className="relative">
          <textarea
            value={segnalazione}
            onChange={handleTextChange}
            className={`w-full border rounded-md focus:outline-none transition-all ${
              showError 
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50' 
                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            } ${isMobile ? 'px-2.5 py-2 text-sm' : 'px-3 py-2'}`}
            rows={isMobile ? "8" : "6"}
            placeholder="Descrivi in dettaglio il problema riscontrato..."
          />
          
          <button
            onClick={handleMicClick}
            className={`absolute ${isMobile ? 'right-1.5 bottom-1.5 p-1.5 text-sm' : 'right-2 bottom-2 p-2'} rounded-full ${
              isRecording ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            <i className={`fa-solid fa-${isRecording ? 'stop' : 'microphone'}`}></i>
          </button>
          
          {isRecording && (
            <div className={`absolute ${isMobile ? 'right-10 bottom-1.5 text-xs' : 'right-12 bottom-2 text-sm'} text-gray-500`}>
              {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>
        
        {/* Messaggio di errore professionale */}
        {showError && (
          <>
            <style>{`
              @keyframes errorShake {
                0%, 100% { transform: translateX(0); }
                10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
                20%, 40%, 60%, 80% { transform: translateX(5px); }
              }
              .error-shake {
                animation: errorShake 0.5s ease-in-out;
              }
            `}</style>
            <div 
              id="segnalazione-error"
              className={`${isMobile ? 'mt-2' : 'mt-3'} error-shake`}
            >
              <div className={`flex items-start gap-2 bg-red-50 border-l-4 border-red-500 rounded-r-lg ${isMobile ? 'p-2.5' : 'p-3'} shadow-sm`}>
                <div className="flex-shrink-0">
                  <div className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} bg-red-100 rounded-full flex items-center justify-center`}>
                    <i className={`fa-solid fa-exclamation-triangle ${isMobile ? 'text-xs' : 'text-sm'} text-red-600`}></i>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className={`font-semibold text-red-800 ${isMobile ? 'text-xs mb-0.5' : 'text-sm mb-1'}`}>
                    Campo Obbligatorio
                  </h4>
                  <p className={`text-red-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    Per favore, descrivi il problema riscontrato prima di procedere. Una descrizione dettagliata ci aiuterà a fornire un'assistenza più efficace.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {!isMobile && (
        <div className="flex justify-between">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors flex items-center"
            onClick={prevStep}
          >
            <i className="fa-solid fa-chevron-left mr-2"></i>
            Indietro
          </button>
          
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center"
            onClick={handleNext}
          >
            Avanti
            <i className="fa-solid fa-chevron-right ml-2"></i>
          </button>
        </div>
      )}
    </div>
  );
};

const ImmaginiStep = ({ formData, setFormData, nextStep, prevStep, isMobile }) => {
  const [images, setImages] = useState(formData.immagini || []);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) {
      toast.error('Puoi caricare al massimo 5 immagini');
      return;
    }

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setImages(prev => [...prev, ...newImages]);
  };

  const rimuoviImmagine = (index) => {
    setImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleNext = () => {
    setFormData(prev => ({
      ...prev,
      immagini: images.map(img => ({
        file: img.file,
        preview: img.preview
      }))
    }));
    
    nextStep();
  };

  return (
    <div className={isMobile ? "p-0" : "p-6"}>
      <h2 className={`font-semibold ${isMobile ? 'text-base mb-3' : 'text-xl mb-6'}`}>Carica Immagini</h2>
      
      <div className={isMobile ? "mb-4" : "mb-8"}>
        <label className={`block font-medium text-gray-700 ${isMobile ? 'text-xs mb-1.5' : 'text-sm mb-2'}`}>
          Carica fino a 5 immagini (opzionale)
        </label>
        
        <div className={`border-2 border-dashed border-gray-300 rounded-lg text-center ${isMobile ? 'p-4' : 'p-6'}`}>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="hidden"
            id="image-upload"
          />
          
          <label
            htmlFor="image-upload"
            className={`cursor-pointer inline-flex items-center border border-transparent font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 ${isMobile ? 'px-3 py-2 text-xs' : 'px-4 py-2 text-sm'}`}
          >
            <i className={`fa-solid fa-upload ${isMobile ? 'mr-1.5' : 'mr-2'}`}></i>
            Seleziona immagini
          </label>
          
          <p className={`text-gray-500 ${isMobile ? 'mt-1.5 text-xs' : 'mt-2 text-sm'}`}>
            Trascina le immagini qui o clicca per selezionarle
          </p>
        </div>
        
        {images.length > 0 && (
          <div className={`grid ${isMobile ? 'mt-3 grid-cols-2 gap-2' : 'mt-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4'}`}>
            {images.map((image, index) => (
              <div key={index} className="relative">
                <img
                  src={image.preview}
                  alt={`Immagine ${index + 1}`}
                  className={`w-full object-cover rounded-lg ${isMobile ? 'h-24' : 'h-32'}`}
                />
                
                <button
                  onClick={() => rimuoviImmagine(index)}
                  className={`absolute bg-red-500 text-white rounded-full hover:bg-red-600 ${isMobile ? 'top-1 right-1 p-0.5 text-xs' : 'top-2 right-2 p-1'}`}
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {!isMobile && (
        <div className="flex justify-between">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors flex items-center"
            onClick={prevStep}
          >
            <i className="fa-solid fa-chevron-left mr-2"></i>
            Indietro
          </button>
          
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors relative"
            onClick={handleNext}
          >
            {uploading ? (
              <>
                <span className="opacity-0">Avanti</span>
                <span className="absolute inset-0 flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              </>
            ) : (
              'Avanti'
            )}
            <i className="fa-solid fa-chevron-right ml-2"></i>
          </button>
        </div>
      )}
    </div>
  );
};

const TipoAssistenzaStep = ({ formData, setFormData, nextStep, prevStep, isMobile }) => {
  const [assistanceTypes, setAssistanceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(formData.tipoAssistenza || null);
  const accountData = useSelector(selectAccountData);
  const hasInitializedRef = useRef(false);

  // Function to select assistance type
  const handleAssistanceTypeSelect = (tipo) => {
    // Se l'impianto è in garanzia, non permettiamo di deselezionare il tipo di assistenza
    if (selectedType && selectedType._id === tipo._id) {
      // Verifichiamo se l'impianto è in garanzia
      if (isSystemUnderWarranty) {
        // Mostriamo un messaggio all'utente
        toast.warning('Non è possibile deselezionare il tipo di assistenza per un impianto in garanzia');
        return; // Interrompiamo l'esecuzione della funzione
      }
      
      // Se l'impianto non è in garanzia, permettiamo la deselezione
      setSelectedType(null);
      setFormData(prev => ({
        ...prev,
        tipoAssistenza: null
      }));
      return;
    }
    
    setSelectedType(tipo);
    setFormData(prev => ({
      ...prev,
      tipoAssistenza: {
        _id: tipo._id,
        nome: tipo.nome,
        ...tipo
      }
    }));
  };

  // Verifichiamo se l'impianto selezionato è in garanzia
  const isSystemUnderWarranty = formData.tipoSistema?.expirationWarrantyDate ? 
    new Date() <= new Date(formData.tipoSistema.expirationWarrantyDate) : false;
  
  // Usiamo questo ref per verificare se abbiamo già gestito la selezione per l'impianto in garanzia
  const handleWarrantyRef = useRef(false);

  useEffect(() => {
    
    const fetchAssistanceTypes = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('installamelo-auth-token');
        const url = `${import.meta.env.VITE_API_BASE_URL}/assistances?accountId=${accountData._id}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'auth-token': token
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Errore nel caricamento dei tipi di assistenza');
        }
        
        const data = await response.json();
        
        // Se l'impianto è in garanzia, mostra solo il tipo di assistenza di default
        if (isSystemUnderWarranty && !handleWarrantyRef.current) {
          // Imposta il flag per evitare la ri-esecuzione
          handleWarrantyRef.current = true;
          
          const defaultType = data.find(type => type.default === true);
          if (defaultType) {
            setAssistanceTypes([defaultType]);
            // Seleziona automaticamente questo tipo di assistenza
            handleAssistanceTypeSelect(defaultType);
          } else {
            setAssistanceTypes([]); // Non ci sono tipi di assistenza di default
            toast.error('Nessun tipo di assistenza di default configurato per gli impianti in garanzia');
          }
        } else if (!isSystemUnderWarranty) {
          // Se l'impianto non è in garanzia, mostra tutti i tipi di assistenza
          setAssistanceTypes(data);
          
          // Se non c'è un tipo selezionato e non abbiamo già inizializzato, seleziona quello con default=true
          if (!selectedType && !hasInitializedRef.current) {
            const defaultType = data.find(type => type.default === true);
            if (defaultType) {
              handleAssistanceTypeSelect(defaultType);
            }
            hasInitializedRef.current = true;
          }
        }
      } catch (error) {
        console.error('Errore nel caricamento dei tipi di assistenza:', error);
        toast.error('Errore nel caricamento dei tipi di assistenza');
      } finally {
        setLoading(false);
      }
    };

    fetchAssistanceTypes();
  }, [accountData._id]); // Rimosse le dipendenze che causavano il loop



  const handleNext = () => {
    if (!selectedType) {
      toast.error('Seleziona un tipo di assistenza');
      return;
    }
    
    nextStep();
  };

  return (
    <div className={isMobile ? "p-0" : "p-6"}>
      <h2 className={`font-semibold ${isMobile ? 'text-base mb-3' : 'text-xl mb-6'}`}>Tipo di Assistenza</h2>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className={`grid grid-cols-1 ${isMobile ? 'gap-3' : 'md:grid-cols-2 gap-6'}`}>
          {sortAssistanceTypes(assistanceTypes).map((tipo) => (
            <div
              key={tipo._id}
              className={`${isMobile ? 'p-3' : 'p-4'} border rounded-lg cursor-pointer transition-colors shadow-sm hover:shadow ${
                selectedType?._id === tipo._id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              style={{
                borderLeftWidth: '4px',
                borderLeftColor: tipo.color || '#3B82F6',
                backgroundColor: selectedType?._id === tipo._id ? 'rgba(59, 130, 246, 0.1)' : 'white'
              }}
              onClick={() => handleAssistanceTypeSelect(tipo)}
            >
              <div className={`flex items-start ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                <div className={`mt-1 ${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} rounded-full border-2 ${isMobile ? 'mr-2' : 'mr-3'} flex-shrink-0 ${
                  selectedType?._id === tipo._id
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}></div>
                <div>
                  <h3 className={`font-medium ${isMobile ? 'text-sm' : 'text-lg'}`}>{tipo.title || tipo.nome} {tipo.label ? `- ${tipo.label}` : ''}</h3>
                  {tipo.description && (
                    <p className={`${isMobile ? 'mt-0.5 text-xs' : 'mt-1 text-sm'} text-gray-600`}>{tipo.description}</p>
                  )}
                </div>
              </div>
              
              <AssistanceTypeServices tipo={tipo} isMobile={isMobile} />
            </div>
          ))}
        </div>
      )}
      
      {!isMobile && (
        <div className="flex justify-between mt-8">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors flex items-center"
            onClick={prevStep}
          >
            <i className="fa-solid fa-chevron-left mr-2"></i>
            Indietro
          </button>
          
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center"
            onClick={handleNext}
            disabled={!selectedType}
          >
            Avanti
            <i className="fa-solid fa-chevron-right ml-2"></i>
          </button>
        </div>
      )}
    </div>
  );
};

const PagamentoStep = ({ formData, setFormData, nextStep, prevStep, confirmSubmit, isSubmitting, isEditMode, isMobile }) => {
  // Stato per la modale di conferma
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const accountData = useSelector(selectAccountData);
  
  // Assicuriamoci che formData sia definito e inizializziamo pagamento con valori di default sicuri
  const safeFormData = formData || {};
  const safePagamento = safeFormData.pagamento || {};
  
  const [pagamento, setPagamento] = useState({
    metodo: safePagamento.metodo || '',
    fasciaOraria: safePagamento.fasciaOraria || '',
    teleassistenza: safePagamento.teleassistenza || false
  });
  
  const [accountSettings, setAccountSettings] = useState(null);
  const [loadingAccountSettings, setLoadingAccountSettings] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(safePagamento.fasciaOraria || 'giornata');
  const [teleassistenza, setTeleassistenza] = useState(safePagamento.teleassistenza || false);
  
  // Determina se il cliente è business (ha la proprietà business = true)
  const isBusinessClient = formData?.cliente?.business === true;
  
  // Carica le impostazioni dell'account
  useEffect(() => {
    if (accountData && accountData.settings) {
      // Log per debug
      console.log('accountData.settings:', accountData.settings);
      console.log('accountData.settings.paymentTypes:', accountData.settings.paymentTypes);
      
      // Usa i dati dell'account già caricati nel redux store
      // Non fornire valori di fallback per evitare di mostrare dati fake
      const paymentMethods = accountData.settings.paymentTypes || [];
      console.log('Payment methods utilizzati:', paymentMethods);
      
      setAccountSettings({
        paymentMethods: paymentMethods
      });
      
      // Se il cliente è privato, gestisci la selezione del metodo di pagamento
      if (!isBusinessClient) {
        // Resetta prima la selezione corrente per assicurarci che non ci siano selezioni multiple
        setSelectedPaymentMethod(null);
        
        // Preferenza 1: Se c'è già un metodo salvato in formData.pagamento, usa quello
        if (safePagamento.metodo) {
          console.log('Utilizzo metodo già selezionato:', safePagamento.metodo);
          setSelectedPaymentMethod(safePagamento.metodo);
        } 
        // Preferenza 2: Altrimenti trova e usa il metodo con default=true
        else {
          const defaultPaymentMethod = paymentMethods.find(method => method.default === true);
          
          if (defaultPaymentMethod) {
            console.log('Seleziono metodo di pagamento default:', defaultPaymentMethod);
            setSelectedPaymentMethod(defaultPaymentMethod._id);
            // Aggiorna anche il formData per mantenere lo stato coerente
            setFormData(prev => ({
              ...prev,
              pagamento: {
                ...prev.pagamento,
                metodo: defaultPaymentMethod._id
              }
            }));
          } 
          // Preferenza 3: Se non c'è un metodo con default=true, usa il primo della lista
          else if (paymentMethods.length > 0) {
            console.log('Nessun metodo default, uso il primo disponibile:', paymentMethods[0]);
            setSelectedPaymentMethod(paymentMethods[0]._id);
            setFormData(prev => ({
              ...prev,
              pagamento: {
                ...prev.pagamento,
                metodo: paymentMethods[0]._id
              }
            }));
          }
        }
      }
      
      // Imposta sempre la fascia oraria a 'giornata' se non è specificata
      if (!safePagamento.fasciaOraria) {
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            fasciaOraria: 'giornata'
          }
        }));
      }
      
      setLoadingAccountSettings(false);
      
      // Se è attiva la teleassistenza, assicurati che sia selezionato solo un metodo di pagamento con carta
      if (safePagamento.teleassistenza && isEditMode) {
        console.log('Richiesta caricata in modalità edit con teleassistenza attiva, applico restrizioni sui metodi di pagamento');
        // Verifica se il metodo di pagamento attuale è compatibile con la teleassistenza
        if (safePagamento.metodo) {
          const currentMethod = paymentMethods.find(m => m._id === safePagamento.metodo);
          if (currentMethod) {
            const methodName = (currentMethod.name || currentMethod.nome || currentMethod.title || currentMethod.label || '').toLowerCase();
            const isCardPayment = methodName.includes('carta') || 
                               methodName.includes('credit') || 
                               methodName.includes('bancomat') || 
                               methodName.includes('pos');
            
            // Se il metodo attuale non è un pagamento con carta, trova un metodo con carta
            if (!isCardPayment) {
              console.log('Metodo di pagamento attuale non compatibile con teleassistenza, cerco un metodo con carta');
              const cardPaymentMethod = paymentMethods.find(method => {
                const name = (method.name || method.nome || method.title || method.label || '').toLowerCase();
                return name.includes('carta') || name.includes('credit') || name.includes('bancomat') || name.includes('pos');
              });
              
              if (cardPaymentMethod) {
                console.log('Trovato metodo di pagamento con carta per teleassistenza:', cardPaymentMethod);
                setSelectedPaymentMethod(cardPaymentMethod._id);
                setFormData(prev => ({
                  ...prev,
                  pagamento: {
                    ...prev.pagamento,
                    metodo: cardPaymentMethod._id
                  }
                }));
              }
            }
          }
        }
      }
    } else {
      // Imposta valori predefiniti se non ci sono impostazioni o non c'è accountData
      const defaultPaymentMethods = [
        { id: 'contanti', nome: 'Contanti', default: true },
        { id: 'carta', nome: 'Carta di Credito/Debito' },
        { id: 'bonifico', nome: 'Bonifico Bancario' }
      ];
      
      setAccountSettings({
        paymentMethods: defaultPaymentMethods
      });
      
      // Se il cliente è privato, gestisci la selezione anche nel caso in cui non ci sono impostazioni dell'account
      if (!isBusinessClient && !selectedPaymentMethod && defaultPaymentMethods.length > 0) {
        console.log('Seleziono metodo di pagamento dai valori predefiniti');
        // Cerca quello con default=true o prendi il primo
        const defaultMethod = defaultPaymentMethods.find(method => method.default === true) || defaultPaymentMethods[0];
        console.log('Metodo selezionato:', defaultMethod);
        setSelectedPaymentMethod(defaultMethod._id);
        // Aggiorna anche il formData
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            metodo: defaultMethod._id
          }
        }));
      }
      
      // Imposta sempre la fascia oraria a 'giornata' se non è specificata
      if (!safePagamento.fasciaOraria) {
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            fasciaOraria: 'giornata'
          }
        }));
      }
      
      setLoadingAccountSettings(false);
    }
  }, [accountData]);

  const formattaData = (dataString) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('it-IT', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const mappaFasciaOraria = (valore) => {
    switch (valore) {
      case 'mattina':
        return 'Mattina (9:00 - 13:00)';
      case 'pomeriggio':
        return 'Pomeriggio (14:00 - 18:00)';
      case 'giornata':
        return 'Giornata intera (9:00 - 18:00)';
      default:
        return valore;
    }
  };

  const formattaTipoAssistenza = (tipo) => {
    if (!tipo) return '';
    return tipo.nome;
  };

  const handlePaymentMethodSelect = (method) => {
    // Prima resetta tutte le selezioni
    setSelectedPaymentMethod(null);
    
    // Poi imposta solo il metodo selezionato
    setSelectedPaymentMethod(method);
    setFormData(prev => ({
      ...prev,
      pagamento: {
        ...prev.pagamento,
        metodo: method
      }
    }));
  };

  const handleTimeSlotSelect = (timeSlot) => {
    if (selectedTimeSlot === timeSlot) {
      setSelectedTimeSlot(null);
      setFormData(prev => ({
        ...prev,
        pagamento: {
          ...prev.pagamento,
          fasciaOraria: null
        }
      }));
      return;
    }
    
    setSelectedTimeSlot(timeSlot);
    setFormData(prev => ({
      ...prev,
      pagamento: {
        ...prev.pagamento,
        fasciaOraria: timeSlot
      }
    }));
  };

  const handleTeleassistenzaChange = (checked) => {
    setTeleassistenza(checked);
    setPagamento(prev => ({
      ...prev,
      teleassistenza: checked
    }));
    
    // Se la teleassistenza è attivata, seleziona automaticamente il metodo di pagamento carta/bancomat
    if (checked && accountSettings?.paymentMethods) {
      // Cerca il metodo di pagamento carta/bancomat
      const cardPaymentMethod = accountSettings.paymentMethods.find(method => {
        const methodName = (method.name || method.nome || method.title || method.label || '').toLowerCase();
        return methodName.includes('carta') || 
               methodName.includes('credit') || 
               methodName.includes('bancomat') || 
               methodName.includes('pos');
      });
      
      // Se trovato, seleziona automaticamente questo metodo
      if (cardPaymentMethod) {
        setSelectedPaymentMethod(cardPaymentMethod._id);
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            teleassistenza: checked,
            metodo: cardPaymentMethod._id
          }
        }));
      } else {
        // Se non trovato, aggiorna comunque il formData con il flag teleassistenza
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            teleassistenza: checked
          }
        }));
      }
    } else {
      // Se disattivata, aggiorna solo il flag teleassistenza
      setFormData(prev => ({
        ...prev,
        pagamento: {
          ...prev.pagamento,
          teleassistenza: checked
        }
      }));
    }
  };

  const handleCardPaymentSelection = (needCardPayment) => {
    if (needCardPayment && accountSettings?.paymentMethods) {
      // Cerca il metodo di pagamento carta/bancomat
      const cardPaymentMethod = accountSettings.paymentMethods.find(method => {
        const methodName = (method.name || method.nome || method.title || method.label || '').toLowerCase();
        return methodName.includes('carta') || 
               methodName.includes('credit') || 
               methodName.includes('bancomat') || 
               methodName.includes('pos');
      });
      
      if (cardPaymentMethod) {
        setSelectedPaymentMethod(cardPaymentMethod._id);
        setFormData(prev => ({
          ...prev,
          pagamento: {
            ...prev.pagamento,
            teleassistenza: needCardPayment,
            metodo: cardPaymentMethod._id
          }
        }));
        return true;
      }
    }
    return false;
  };

  const handleNext = () => {
    // Per clienti privati è obbligatorio il metodo di pagamento, per business no
    const isBusinessClient = formData?.cliente?.business === true;
    
    if (!isBusinessClient && !selectedPaymentMethod) {
      toast.error('Seleziona un metodo di pagamento');
      return;
    }
    
    // Assicuriamoci che la fascia oraria sia impostata, default a 'giornata'
    if (!selectedTimeSlot) {
      setSelectedTimeSlot('giornata');
      setFormData(prev => ({
        ...prev,
        pagamento: {
          ...prev.pagamento,
          fasciaOraria: 'giornata'
        }
      }));
    }
    
    // Mostra la modale di conferma invece di procedere direttamente
    setShowConfirmModal(true);
  };
  
  const handleConfirm = () => {
    // Chiudi la modale e procedi con la sottomissione
    setShowConfirmModal(false);
    confirmSubmit();
  };
  
  const handleCancel = () => {
    // Chiudi solo la modale
    setShowConfirmModal(false);
  };

  return (
    <div className={isMobile ? "p-0" : "p-6"}>
      <h2 className={`font-semibold ${isMobile ? 'text-base mb-3' : 'text-xl mb-6'}`}>Pagamento e Appuntamento</h2>
      
      {loadingAccountSettings ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : accountSettings ? (
        <>
          {/* Opzione per teleassistenza */}
          <div className={`bg-blue-50 rounded-lg border border-blue-200 ${isMobile ? 'mb-4 p-3' : 'mb-8 p-4'}`}>
            <div className="flex items-start">
              <input
                type="checkbox"
                id="teleassistenza"
                checked={teleassistenza}
                onChange={(e) => handleTeleassistenzaChange(e.target.checked)}
                className={`${isMobile ? 'h-4 w-4 mt-0.5' : 'h-5 w-5'} text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0`}
              />
              <label htmlFor="teleassistenza" className={`cursor-pointer ${isMobile ? 'ml-2' : 'ml-3'}`}>
                <span className={`font-medium text-gray-800 ${isMobile ? 'text-sm' : ''}`}>Richiedi Teleassistenza</span>
                <p className={`text-gray-600 ${isMobile ? 'text-xs mt-0.5' : 'text-sm mt-1'}`}>Seleziona questa opzione se desideri richiedere supporto tecnico da remoto</p>
              </label>
            </div>
            {/* Disclaimer pagamento anticipato per teleassistenza */}
            <div className={`mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg`}>
              <div className="flex items-start">
                <i className={`fa-solid fa-triangle-exclamation text-amber-600 ${isMobile ? 'text-sm mt-0.5' : 'mt-0.5'}`}></i>
                <p className={`text-amber-800 ${isMobile ? 'text-xs ml-2' : 'text-sm ml-3'}`}>
                  <strong>Nota:</strong> La modalità teleassistenza prevede il pagamento anticipato. 
                  Il cliente verrà contattato dall'amministrazione di Securfox per completare il pagamento anticipato.
                </p>
              </div>
            </div>
          </div>

          <div className={isMobile ? "mb-4" : "mb-8"}>
            <h3 className={`font-medium ${isMobile ? 'text-sm mb-2' : 'text-lg mb-4'}`}>Metodo di Pagamento</h3>
            
            {isBusinessClient ? (
              // Box informativo per clienti business
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center">
                  <i className="fa-solid fa-building-user text-gray-600 mr-3"></i>
                  <div>
                    <h4 className="font-medium">Cliente Business</h4>
                    <p className="text-sm text-gray-600">Il metodo di pagamento verrà impostato in base al contratto cliente.</p>
                    {formData?.cliente?.vatNumber && (
                      <p className="text-sm text-gray-600 mt-1">P.IVA: {formData.cliente.vatNumber}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Selezione dei metodi di pagamento per clienti privati
              <div className={`grid grid-cols-1 ${isMobile ? 'gap-2' : 'md:grid-cols-2 gap-4'}`}>
                {accountSettings?.paymentMethods?.map((method) => {
                  // Determina se è un metodo di pagamento con carta
                  const methodName = (method.name || method.nome || method.title || method.label || '').toLowerCase();
                  const isCardPayment = methodName.includes('carta') || 
                                       methodName.includes('credit') || 
                                       methodName.includes('bancomat') || 
                                       methodName.includes('pos');
                  
                  // Se è attiva la teleassistenza, mostra solo metodi pagamento con carta
                  // Se non è attiva la teleassistenza, mostra tutti i metodi
                  const shouldShow = !teleassistenza || (teleassistenza && isCardPayment);
                  
                  // Se il metodo non deve essere mostrato, salta questo elemento
                  if (!shouldShow) return null;
                  
                  return (
                    <div
                      key={method._id}
                      className={`${isMobile ? 'p-3' : 'p-4'} border rounded-lg cursor-pointer transition-colors ${
                        selectedPaymentMethod === method._id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      } ${
                        teleassistenza && isCardPayment ? 'ring-2 ring-blue-300' : ''
                      }`}
                      onClick={() => handlePaymentMethodSelect(method._id)}
                    >
                      <div className="flex items-center">
                        <div className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} rounded-full border-2 ${isMobile ? 'mr-2' : 'mr-3'} flex-shrink-0 ${
                          selectedPaymentMethod === method._id
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}></div>
                        <h4 className={`font-medium ${isMobile ? 'text-sm' : ''}`}>{method.name || method.nome || method.title || method.label}</h4>
                        {teleassistenza && isCardPayment && (
                          <span className={`${isMobile ? 'ml-1 text-[10px]' : 'ml-2 text-xs'} bg-blue-100 text-blue-800 px-2 py-1 rounded-full`}>
                            {isMobile ? 'Obbligatorio' : 'Obbligatorio per teleassistenza'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className={isMobile ? "mb-4" : "mb-8"}>
            <h3 className={`font-medium ${isMobile ? 'text-sm mb-2' : 'text-lg mb-4'}`}>Fascia Oraria</h3>
            <div className={`grid grid-cols-1 ${isMobile ? 'gap-2' : 'md:grid-cols-3 gap-4'}`}>
              {['mattina', 'pomeriggio', 'giornata'].map((timeSlot) => (
                <div
                  key={timeSlot}
                  className={`${isMobile ? 'p-3' : 'p-4'} border rounded-lg cursor-pointer transition-colors ${
                    selectedTimeSlot === timeSlot
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => handleTimeSlotSelect(timeSlot)}
                >
                  <div className="flex items-center">
                    <div className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} rounded-full border-2 ${isMobile ? 'mr-2' : 'mr-3'} flex-shrink-0 ${
                      selectedTimeSlot === timeSlot
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}></div>
                    <h4 className={`font-medium ${isMobile ? 'text-sm' : ''}`}>{mappaFasciaOraria(timeSlot)}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Rimossa la sezione Data come richiesto */}
        </>
      ) : (
        <div className="p-4 text-center">
          <p>Nessuna impostazione di pagamento disponibile</p>
        </div>
      )}
      
      {!isMobile && (
        <div className="flex justify-between">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors flex items-center"
            onClick={prevStep}
          >
            <i className="fa-solid fa-chevron-left mr-2"></i>
            Indietro
          </button>
          
          <button
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center"
            onClick={handleNext}
            disabled={!selectedPaymentMethod || !selectedTimeSlot}
          >
            {isEditMode ? 'Salva modifiche' : 'Crea nuova assistenza'}
            <i className={`fa-solid ${isEditMode ? 'fa-save' : 'fa-plus-circle'} ml-2`}></i>
          </button>
        </div>
      )}
      
      {/* Pulsante Mobile per ultimo step */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-3 py-2.5 shadow-lg">
          <button
            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center text-sm relative"
            onClick={handleNext}
            disabled={!selectedPaymentMethod || !selectedTimeSlot || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="opacity-0">{isEditMode ? 'Salva modifiche' : 'Crea Assistenza'}</span>
                <span className="absolute inset-0 flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              </>
            ) : (
              <>
                {isEditMode ? 'Salva modifiche' : 'Crea Assistenza'}
                <i className={`fa-solid ${isEditMode ? 'fa-save' : 'fa-plus-circle'} ml-2`}></i>
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Modale di conferma */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl shadow-2xl max-w-md w-full ${isMobile ? 'p-5' : 'p-6'}`}>
            {/* Icona intestazione */}
            <div className={`flex justify-center ${isMobile ? 'mb-3' : 'mb-4'}`}>
              <div className={`${isMobile ? 'w-14 h-14' : 'w-16 h-16'} bg-blue-100 rounded-full flex items-center justify-center`}>
                <i className={`fa-solid fa-circle-question ${isMobile ? 'text-2xl' : 'text-3xl'} text-blue-600`}></i>
              </div>
            </div>

            {/* Titolo */}
            <h3 className={`font-bold text-center ${isMobile ? 'text-lg mb-2' : 'text-xl mb-3'} text-gray-800`}>
              {isEditMode ? 'Modifica richiesta di assistenza' : 'Conferma richiesta di assistenza'}
            </h3>

            {/* Testo */}
            <p className={`text-center text-gray-600 ${isMobile ? 'text-sm mb-5' : 'text-base mb-6'}`}>
              {isEditMode ? 'Sei sicuro di voler aggiornare questa richiesta di assistenza?' : 'Sei sicuro di voler creare questa richiesta di assistenza?'}
            </p>
            
            {/* Pulsanti */}
            <div className={`flex ${isMobile ? 'flex-col-reverse gap-2' : 'flex-row gap-3'}`}>
              <button
                type="button"
                onClick={handleCancel}
                className={`${isMobile ? 'w-full' : 'flex-1'} ${isMobile ? 'py-2.5' : 'py-2.5'} px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium border border-gray-200 ${isMobile ? 'text-sm' : ''}`}
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isSubmitting}
                className={`${isMobile ? 'w-full' : 'flex-1'} ${isMobile ? 'py-2.5' : 'py-2.5'} px-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg transition-all font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed relative flex items-center justify-center ${isMobile ? 'text-sm' : ''}`}
              >
                {isSubmitting ? (
                  <>
                    <span className="opacity-0">{isEditMode ? 'Salva modifiche' : 'Crea nuova assistenza'}</span>
                    <span className="absolute inset-0 flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                  </>
                ) : (
                  <>
                    {isEditMode ? (
                      <>
                        <i className={`fa-solid fa-save ${isMobile ? 'mr-1.5' : 'mr-2'}`}></i>
                        Salva modifiche
                      </>
                    ) : (
                      <>
                        <i className={`fa-solid fa-plus-circle ${isMobile ? 'mr-1.5' : 'mr-2'}`}></i>
                        Crea nuova assistenza
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const iconClass = (icon) => {
  return `fa-solid fa-${icon}`;
};

const StepIcon = ({ icon, isActive, isCompleted }) => {
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
      isActive ? 'bg-blue-500 text-white' : isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
    }`}>
      <i className={iconClass(icon)}></i>
    </div>
  );
};

const AssistancePreview = ({ formData, currentStep, submitForm }) => {
  // Accesso ai dati dell'account tramite Redux
  const accountData = useSelector(selectAccountData);
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const mappaFasciaOraria = (valore) => {
    switch (valore) {
      case 'mattina':
        return 'Mattina (9:00 - 13:00)';
      case 'pomeriggio':
        return 'Pomeriggio (14:00 - 18:00)';
      case 'giornata':
        return 'Giornata intera (9:00 - 18:00)';
      default:
        return valore;
    }
  };

  const formattaTipoAssistenza = (tipo) => {
    if (!tipo) return '';
    return tipo.nome;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-3">
      <div className="space-y-4">
        <div className="mb-5">
          <div className="progress h-2 bg-gray-200 mb-2">
            <div 
              className="progress-bar bg-blue-500 h-full transition-all duration-500" 
              style={{ width: `${Math.min(100, (Object.keys(formData).filter(key => {
                if (key === 'pagamento') return formData[key].metodo && formData[key].data && formData[key].fasciaOraria;
                if (key === 'immagini') return true; // Le immagini sono opzionali
                if (key === 'sistema') return formData[key] && formData.tipoSistema;
                return formData[key];
              }).length / 4) * 100)}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-500 text-right">
            {currentStep === 4 && formData.tipoAssistenza && formData.cliente && formData.segnalazione ? 
              'Completa gli ultimi dettagli per inviare la richiesta' : 
              'Completa tutti i campi richiesti'}
          </p>
        </div>

        {/* Box Cliente rimosso su richiesta dell'utente */}

        <div className={`mb-2 p-2 rounded-md ${formData.sistema ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'}`}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-cogs mr-2 text-blue-500"></i>
            Sistema
          </h4>
          {formData.sistema ? (
            <div>
              <p className="font-medium">{formData.sistema.title || formData.sistema.nome}</p>
              {formData.sistema.customerAddressLabel && (
                <p className="text-sm text-gray-600">
                  <i className="fa-solid fa-location-dot mr-1 text-gray-400"></i>
                  {formData.sistema.customerAddressLabel}
                </p>
              )}
              {formData.tipoSistema && (
                <div className="mt-2 pt-2 border-t border-blue-100">
                  <p className="font-medium">Tipo di sistema: {formData.tipoSistema.typeLabel}</p>
                  <div className="grid grid-cols-1 gap-x-2 text-xs mt-1">
                    {formData.tipoSistema.testedDate && (
                      <p className="mb-1">Data collaudo: {formatDate(formData.tipoSistema.testedDate)}</p>
                    )}
                    {formData.tipoSistema.expirationWarrantyDate && (
                      <div className="flex items-center mb-1">
                        <span className="mr-1">Garanzia:</span>
                        <span className={new Date() > new Date(formData.tipoSistema.expirationWarrantyDate) ? 'text-red-500' : 'text-green-500 font-medium'}>
                          {new Date() > new Date(formData.tipoSistema.expirationWarrantyDate) ? 
                            'Fuori garanzia' : 
                            'In garanzia'}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">
                          {new Date() > new Date(formData.tipoSistema.expirationWarrantyDate) ? 
                            `(Scaduta il ${formatDate(formData.tipoSistema.expirationWarrantyDate)})` : 
                            `(Scade il ${formatDate(formData.tipoSistema.expirationWarrantyDate)})`
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Seleziona un sistema</p>
          )}
        </div>

        <div className={`mb-2 p-2 rounded-md ${formData.segnalazione ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'}`}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-comment mr-2 text-blue-500"></i>
            Descrizione
          </h4>
          {formData.segnalazione ? (
            <p className="text-sm">{formData.segnalazione.length > 100 ? formData.segnalazione.substring(0, 100) + '...' : formData.segnalazione}</p>
          ) : (
            <p className="text-sm text-gray-500 italic">Aggiungi una descrizione del problema</p>
          )}
        </div>
        
        {/* Rimosso la sezione Categorie dalla preview come richiesto */}

        <div className={`mb-2 p-2 rounded-md ${formData.immagini && formData.immagini.length > 0 ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'}`}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-image mr-2 text-blue-500"></i>
            Immagini
          </h4>
          {formData.immagini && formData.immagini.length > 0 ? (
            <div>
              <p className="text-sm">{formData.immagini.length} {formData.immagini.length === 1 ? 'immagine caricata' : 'immagini caricate'}</p>
              {formData.immagini.length > 0 && (
                <div className="flex mt-2 flex-wrap gap-2">
                  {formData.immagini.slice(0, 3).map((img, idx) => (
                    <div key={idx} className="w-16 h-16 relative rounded overflow-hidden">
                      <img src={img.preview} alt={`Anteprima ${idx+1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {formData.immagini.length > 3 && (
                    <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-gray-600">
                      +{formData.immagini.length - 3}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Nessuna immagine caricata (opzionale)</p>
          )}
        </div>

        <div 
          className={`mb-2 p-2 rounded-md relative overflow-hidden ${formData.tipoAssistenza ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'}`}
          style={formData.tipoAssistenza && formData.tipoAssistenza.color ? {
            borderLeftWidth: '4px',
            borderLeftColor: formData.tipoAssistenza.color
          } : {}}
        >
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-wrench mr-2 text-blue-500"></i>
            Tipo di Assistenza
          </h4>
          {formData.tipoAssistenza ? (
            <div>
              <p className="font-medium">{formData.tipoAssistenza.title || formData.tipoAssistenza.nome}</p>
              {formData.tipoAssistenza.description && (
                <p className="text-sm text-gray-600 mb-2">{formData.tipoAssistenza.description}</p>
              )}
              
              {/* Mostra il badge di teleassistenza se è flaggata */}
              {formData.pagamento && formData.pagamento.teleassistenza && (
                <div className="mt-1 mb-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                    <i className="fa-solid fa-video mr-1 text-green-600"></i>
                    Teleassistenza
                  </span>
                </div>
              )}
              
              {/* Servizi inclusi: stesso componente della card */}
              <AssistanceTypeServices tipo={formData.tipoAssistenza} compact />
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Seleziona un tipo di assistenza</p>
          )}
        </div>

        <div className={`mb-2 p-2 rounded-md ${formData.pagamento && formData.pagamento.metodo ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'}`}>
          <h4 className="text-md font-semibold mb-2 flex items-center">
            <i className="fa-solid fa-credit-card mr-2 text-blue-500"></i>
            Pagamento e Appuntamento
          </h4>
          {formData.pagamento && formData.pagamento.metodo ? (
            <div>
              <p className="text-sm"><strong>Metodo:</strong> {
                // Recupera il nome del metodo di pagamento dall'ID
                (() => {
                  if (!formData.pagamento.metodo) return 'Non specificato';
                  
                  // Cerca nelle impostazioni dell'account
                  const paymentMethod = accountData?.settings?.paymentTypes?.find(m => m._id === formData.pagamento.metodo);
                  if (paymentMethod) return paymentMethod.name || paymentMethod.nome || paymentMethod.title || paymentMethod.label;
                  
                  // Se non trovato, mostra almeno l'ID
                  return formData.pagamento.metodo;
                })()
              }</p>
              {formData.pagamento.data && (
                <p className="text-sm"><strong>Data:</strong> {formatDate(formData.pagamento.data)}</p>
              )}
              {formData.pagamento.fasciaOraria && (
                <p className="text-sm"><strong>Fascia oraria:</strong> {mappaFasciaOraria(formData.pagamento.fasciaOraria)}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Dati di pagamento e appuntamento non inseriti</p>
          )}
        </div>

        {/* Mostra il pulsante solo nell'ultimo step (Pagamento) quando tutti i dati sono completi */}
        {currentStep === 4 && formData.cliente && formData.segnalazione && formData.tipoAssistenza && 
         formData.pagamento && formData.pagamento.metodo && formData.pagamento.data && formData.pagamento.fasciaOraria && (
          <button
            className="w-full py-3 px-4 bg-green-500 hover:bg-green-600 text-white font-medium rounded-md transition-colors flex items-center justify-center mt-4"
            onClick={submitForm}
          >
            <i className="fa-solid fa-check-circle mr-2"></i>
            Conferma e Invia
          </button>
        )}
      </div>
    </div>
  );
};

const CustomerAddEditAssistanceRequest = () => {
  const user = useSelector(selectUser);
  const [customerData, setCustomerData] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const accountData = useSelector(selectAccountData);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false); // Stato per gestire il caricamento
  const [isEditMode, setIsEditMode] = useState(false); // Stato per la modalità di modifica
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSuccessModal, setShowSuccessModal] = useState(false); // Modale di successo
  const [formData, setFormData] = useState({
    cliente: null,
    segnalazione: '',
    immagini: [],
    tipoAssistenza: null,
    pagamento: {
      metodo: '',
      fasciaOraria: '',
      data: ''
    }
  });

  // Ridefiniamo gli step del wizard
  const steps = [
    { title: 'Scegli Impianto', icon: 'cogs' },
    { title: 'Segnalazione', icon: 'comment' },
    { title: 'Immagini', icon: 'image' },
    { title: 'Tipo Assistenza', icon: 'wrench' },
    { title: 'Pagamento', icon: 'credit-card' }
  ];

  // Rilevamento resize per mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Effetto per caricare i dati del cliente usando customerId dal localStorage
  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setLoadingCustomer(true);
        
        // Recupera i dati utente dal localStorage
        const token = localStorage.getItem('installamelo-auth-token');
        // Il customerId e accountId sono salvati direttamente in auth-user
        const userData = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
        const customerId = userData.customerId;
        const accountId = userData.accountId;
        
        console.log('Dati utente da localStorage:', { userData, token: token ? 'presente' : 'assente' });
        
        console.log('Dati utente dal localStorage:', { userData, customerId, accountId });
        
        if (!customerId || !accountId || !token) {
          console.error('Dati mancanti per il caricamento del cliente:', { customerId, accountId, hasToken: !!token });
          toast.error('Impossibile caricare i dati del cliente. Effettua nuovamente il login.');
          setLoadingCustomer(false);
          return;
        }
        
        console.log('Caricamento dati cliente con:', { customerId, accountId });
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/customers?customerId=${customerId}&accountId=${accountId}`, {
          headers: {
            'Content-Type': 'application/json',
            'auth-token': token
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Errore nel caricamento dei dati cliente');
        }
        
        const data = await response.json();
        console.log('Dati cliente ricevuti:', data);
        
        // I dati possono arrivare direttamente come oggetto o come array
        const customerInfo = Array.isArray(data) && data.length > 0 ? data[0] : data;
        
        if (customerInfo && customerInfo._id) {
          console.log('Cliente trovato e valido:', customerInfo);
          setCustomerData(customerInfo);
          
          // Prepara i dati del cliente per il formData
          const customerName = customerInfo.businessName || customerInfo.name || customerInfo.fullName || customerInfo.surname;
          
          // Imposta il cliente nel formData - questo simulerà la selezione del cliente come primo step
          const clienteData = {
            id: customerInfo._id,
            nome: customerName,
            vatNumber: customerInfo.vatNumber,
            email: customerInfo.email,
            phone: customerInfo.phone,
            // Aggiungi altri dati utili del cliente
            addresses: customerInfo.addresses || [],
            // Questi campi sono necessari per il funzionamento del componente SegnalazioneStep
            label: customerName,
            value: customerInfo._id,
            data: customerInfo
          };
          
          console.log('Dati cliente preparati per il formData:', clienteData);
          
          setFormData(prev => ({
            ...prev,
            cliente: clienteData
          }));
          
          console.log('Cliente impostato nel formData:', {
            id: customerInfo._id,
            nome: customerInfo.businessName || customerInfo.name || customerInfo.fullName
          });
          
          // Imposta il currentStep a 0 (Segnalazione) se non in modalità modifica
          if (!isEditMode) {
            setCurrentStep(0);
          }
        } else {
          console.error('Nessun dato cliente trovato');
          toast.error('Non è stato possibile trovare i dati del cliente');
        }
      } catch (error) {
        console.error('Errore nel caricamento dei dati cliente:', error);
        toast.error('Errore nel caricamento dei dati cliente: ' + (error.message || 'Errore sconosciuto'));
      } finally {
        setLoadingCustomer(false);
      }
    };
    
    fetchCustomerData();
  }, [isEditMode]);
  
  // Effetto per caricare i dati della richiesta se siamo in modalità modifica
  useEffect(() => {
    const loadExistingRequest = async () => {
      // Controlla se siamo in modalità modifica (c'è un id nell'URL o nel location state)
      if (id || (location.state && location.state.requestData)) {
        setIsEditMode(true);
        
        // Utilizza i dati dal location state se disponibili
        if (location.state && location.state.requestData) {
          const requestData = location.state.requestData;
          console.log('Caricamento dati dalla request esistente:', requestData);
          
          // Mappa i dati della richiesta nel formato utilizzato da formData
          setFormData({
            cliente: {
              id: requestData.customerId,
              nome: requestData.customerLabel,
              business: requestData.customerBusiness
            },
            sistema: {
              _id: requestData.systemId,
              title: requestData.systemLabel,
              nome: requestData.systemLabel,
              customerAddressId: requestData.systemAddressId,
              customerAddressLabel: requestData.systemAddressLabel
            },
            tipoSistema: {
              _id: requestData.systemTypeId,
              typeLabel: requestData.systemTypeLabel,
              expirationWarrantyDate: requestData.systemTypeExpirationWarrantyDate
            },
            segnalazione: requestData.customerRequest || requestData.description || '',
            analisiRichiesta: requestData.analisiRichiesta,
            analisiInfo: requestData.analisiInfo,
            useCases: requestData.useCases || [],
            immagini: Array.isArray(requestData.uploads) 
              ? requestData.uploads.map((url, index) => ({
                  id: `upload-${index}`,
                  file: null, // Non abbiamo il file originale
                  preview: url,
                  url: url
                }))
              : [],
            tipoAssistenza: {
              _id: requestData.assistanceTypeId,
              title: requestData.assistanceTypeLabel,
              nome: requestData.assistanceTypeLabel,
              description: requestData.assistanceTypeDescription,
              priority: {
                _id: requestData.assistanceTypePriorityId,
                priorityLabel: requestData.assistanceTypePriorityLabel,
                priorityValue: requestData.assistanceTypePriorityValue
              }
            },
            pagamento: {
              metodo: requestData.paymentTypeId,
              fasciaOraria: requestData.appointmentTimeSlot || requestData.fasciaOraria || 'giornata',
              data: requestData.plannedDate,
              teleassistenza: requestData.teleassistenza || false
            },
            isEditMode: true
          });
          
          return;
        }
        
        // Se non ci sono dati nel location state ma c'è un ID, carica i dati dall'API
        if (id) {
          try {
            const token = localStorage.getItem('installamelo-auth-token');
            if (!token) {
              toast.error('Errore: Sessione scaduta. Effettua nuovamente l\'accesso.');
              return;
            }
            
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/assistanceRequests/${id}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'auth-token': token
              }
            });
            
            if (!response.ok) {
              throw new Error('Errore nel caricamento della richiesta');
            }
            
            const requestData = await response.json();
            console.log('Richiesta caricata da API:', requestData);
            
            // Mappatura analoga a quella sopra
            setFormData({
              cliente: {
                id: requestData.customerId,
                nome: requestData.customerLabel,
                business: requestData.customerBusiness
              },
              sistema: {
                _id: requestData.systemId,
                title: requestData.systemLabel,
                nome: requestData.systemLabel,
                customerAddressId: requestData.systemAddressId,
                customerAddressLabel: requestData.systemAddressLabel
              },
              tipoSistema: {
                _id: requestData.systemTypeId,
                typeLabel: requestData.systemTypeLabel,
                expirationWarrantyDate: requestData.systemTypeExpirationWarrantyDate
              },
              segnalazione: requestData.customerRequest || requestData.description || '',
              analisiRichiesta: requestData.analisiRichiesta,
              analisiInfo: requestData.analisiInfo,
              useCases: requestData.useCases || [],
              immagini: Array.isArray(requestData.uploads) 
                ? requestData.uploads.map((url, index) => ({
                    id: `upload-${index}`,
                    file: null,
                    preview: url,
                    url: url
                  }))
                : [],
              tipoAssistenza: {
                _id: requestData.assistanceTypeId,
                title: requestData.assistanceTypeLabel,
                nome: requestData.assistanceTypeLabel,
                description: requestData.assistanceTypeDescription,
                priority: {
                  _id: requestData.assistanceTypePriorityId,
                  priorityLabel: requestData.assistanceTypePriorityLabel,
                  priorityValue: requestData.assistanceTypePriorityValue
                }
              },
              pagamento: {
                metodo: requestData.paymentTypeId,
                fasciaOraria: requestData.appointmentTimeSlot || requestData.fasciaOraria || 'giornata',
                data: requestData.plannedDate
              },
              isEditMode: true
            });
          } catch (error) {
            console.error('Errore durante il caricamento della richiesta:', error);
            toast.error('Impossibile caricare i dati della richiesta');
            navigate('/assistanceRequestsNoAI');
          }
        }
      }
    };
    
    loadExistingRequest();
  }, [id, location.state, navigate]);

  const handleNext = () => {
    // Validazione dello step corrente prima di procedere
    if (currentStep === 1) {
      // Step Segnalazione - descrizione obbligatoria
      if (!formData.segnalazione || !formData.segnalazione.trim()) {
        // Triggera la visualizzazione dell'errore nello step
        // Creiamo un evento custom per comunicare con lo step
        const event = new CustomEvent('showSegnalazioneError');
        window.dispatchEvent(event);
        return; // Blocca l'avanzamento
      }
    }
    
    setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleFormDataUpdate = (newData) => {
    setFormData(prev => ({
      ...prev,
      ...newData
    }));
  };

  const submitForm = async () => {
    setIsSubmitting(true); // Attiva lo stato di caricamento
    try {
      // Determina se siamo in modalità creazione o modifica
      const isCreatingNew = !isEditMode;
      // Utilizza l'ID dell'account (già recuperato all'inizio del componente) e il token di autenticazione
      const token = localStorage.getItem('installamelo-auth-token');
      
      if (!token) {
        toast.error('Errore: Sessione scaduta. Effettua nuovamente l\'accesso.');
        return;
      }
      
      // Funzione per caricare una singola immagine sul server
      const uploadImage = async (image) => {
        // Se l'immagine è già un URL o una stringa, la restituiamo direttamente
        if (typeof image === 'string') {
          console.log('Immagine già in formato URL:', image);
          return image;
        }
        
        // Determiniamo se l'oggetto è un File diretto o un oggetto che contiene un File
        let fileObject;
        let titleText;
        
        if (image instanceof File) {
          // È direttamente un oggetto File
          console.log('Immagine è un oggetto File diretto:', image.name);
          fileObject = image;
          titleText = image.name;
        } else if (image && image.file instanceof File) {
          // È un oggetto che contiene un File nella proprietà 'file'
          console.log('Immagine è un oggetto con File:', image.file.name);
          fileObject = image.file;
          titleText = image.title || image.file.name;
        } else {
          console.warn('Formato immagine non valido, né File né oggetto con file:', image);
          return null;
        }
        
        // Verificare il tipo di file
        const fileName = fileObject.name;
        const ext = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
        
        // Controlla che sia un'immagine supportata
        if (!(ext === "gif" || ext === "png" || ext === "jpeg" || ext === "jpg" || ext === "pdf")) {
          console.warn(`Tipo di file non consentito: ${ext}`);
          toast.error("Tipo di File non consentito!");
          return null;
        }
        
        try {
          // Prepara i dati per il caricamento
          const data = new FormData();
          data.append('file', fileObject, fileName);
          data.append('uploadName', fileName);
          data.append('uploadTitle', titleText);
          data.append('path', `assistanceRequests/${accountData._id}/${formData.cliente.id}`);
          
          console.log(`Tentativo di caricamento dell'immagine ${fileName}...`);
          
          // Chiamata per caricare l'immagine
          const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/files/upload`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'auth-token': token
            },
            body: data
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Errore nell'upload dell'immagine: ${errorText}`);
          }
          
          const result = await response.json();
          console.log('Immagine caricata con successo:', result);
          return result.url || result.fileUrl || result; // Restituisce l'URL dell'immagine caricata
        } catch (error) {
          console.error(`Errore durante il caricamento dell'immagine:`, error);
          throw error;
        }
      };
      
      // 1. Prima carichiamo tutte le immagini e otteniamo i loro URL
      let uploadedImageUrls = [];
      
      if (Array.isArray(formData.immagini) && formData.immagini.length > 0) {
        console.log(`Caricamento di ${formData.immagini.length} immagini...`, formData.immagini);
        
        // Mostriamo un messaggio di caricamento
        toast.info("Caricamento delle immagini in corso...", { autoClose: false, toastId: 'uploading-images' });
        
        // Carichiamo le immagini una per una
        for (const img of formData.immagini) {
          try {
            const url = await uploadImage(img);
            if (url) {
              uploadedImageUrls.push(url);
              console.log(`Immagine caricata: ${url}`);
            }
          } catch (imgError) {
            console.error(`Errore per una singola immagine:`, imgError);
            // Continuiamo con le altre immagini anche se una fallisce
          }
        }
        
        // Aggiorniamo l'utente sui risultati
        toast.dismiss('uploading-images');
        if (uploadedImageUrls.length > 0) {
          toast.success(`${uploadedImageUrls.length} immagini caricate con successo`);
          console.log('Immagini caricate:', uploadedImageUrls);
        } else {
          toast.warning("Nessuna immagine caricata con successo");
        }
      }
      
      // In modalità modifica, preserviamo alcuni campi come il codice e lo stato attuale
      const assistanceRequest = {
        // Dati account e cliente
        accountId: accountData._id,
        customerId: formData.cliente.id,
        customerLabel: formData.cliente.nome,
        
        // Dati sistema
        systemId: formData.sistema?._id,
        systemLabel: formData.sistema?.title || formData.sistema?.nome,
        systemAddressId: formData.sistema?.customerAddressId,
        systemAddressLabel: formData.sistema?.customerAddressLabel,
        
        // Dati tipo sistema
        systemTypeId: formData.tipoSistema?._id,
        systemTypeLabel: formData.tipoSistema?.typeLabel,
        systemTypeWarranty: formData.tipoSistema?.expirationWarrantyDate ? 
          new Date() <= new Date(formData.tipoSistema.expirationWarrantyDate) : false,
        systemTypeExpirationWarrantyDate: formData.tipoSistema?.expirationWarrantyDate,
        
        // Dati richiesta
        customerRequest: formData.segnalazione,
        description: formData.segnalazione, // Aggiungo il campo description con la segnalazione
        useCases: formData.useCases || [],
        
        // Dati analisi AI (se presenti, da SegnalazioneStep)
        analisiRichiesta: formData.analisiRichiesta,
        analisiInfo: formData.analisiInfo,
        
        // Dati tipo assistenza
        assistanceTypeId: formData.tipoAssistenza._id,
        assistanceTypeLabel: formData.tipoAssistenza.title || formData.tipoAssistenza.nome,
        assistanceTypeDescription: formData.tipoAssistenza.description,
        // Dati priorità del tipo assistenza
        assistanceTypePriorityId: formData.tipoAssistenza?.priority?._id || '',
        assistanceTypePriorityLabel: formData.tipoAssistenza?.priority?.priorityLabel || '',
        assistanceTypePriorityValue: formData.tipoAssistenza?.priority?.priorityValue || '',
        
        // Dati pagamento e appuntamento
        paymentTypeId: formData.pagamento.metodo,
        paymentTypeLabel: (() => {
          if (!formData.pagamento.metodo) return '';
          const paymentMethod = accountData?.settings?.paymentTypes?.find(m => m._id === formData.pagamento.metodo);
          return paymentMethod ? 
            (paymentMethod.name || paymentMethod.nome || paymentMethod.title || paymentMethod.label) : 
            formData.pagamento.metodo;
        })(),
        appointmentTimeSlot: formData.pagamento.fasciaOraria,
        // Flag teleassistenza
        teleassistenza: formData.pagamento.teleassistenza || false,
        // Il campo plannedDate è opzionale, lo includiamo solo se valorizzato
        ...(formData.pagamento.data ? { plannedDate: formData.pagamento.data } : {}),
        
        // Immagini caricate - usiamo gli URL ottenuti dal server
        uploads: uploadedImageUrls,
        
        // Data creazione
        creationDate: new Date().toISOString(),
        
        // Stato iniziale - ricava lo stato predefinito dalle impostazioni account
        ...(() => {
          // Trova lo stato predefinito nell'array assistanceRequestStatus
          const defaultStatus = accountData?.settings?.assistanceRequestStatus?.find(status => status.default === true);
          console.log('Stato predefinito trovato:', defaultStatus);
          
          // Restituisci i campi ID e label in un oggetto
          return {
            statusId: defaultStatus?._id || 'new',
            statusLabel: defaultStatus?.label || 'Nuova'
          };
        })(),
        
        // GeoData richiesto dallo schema
        geoData: {
          type: "Point",
          coordinates: [10.1234, 45.5678] // Coordinata di default
        }
      };
      
      // Per la modalità edit, dobbiamo includere _id nell'oggetto assistanceRequest
      if (!isCreatingNew && id) {
        assistanceRequest._id = id;
      }
      
      console.log('Oggetto assistanceRequest creato:', JSON.stringify(assistanceRequest));
      
      // 3. Chiamata API principale in formato JSON
      // L'API si aspetta che l'oggetto assistanceRequest sia incluso in un oggetto wrapper
      const apiUrl = isCreatingNew
        ? `${import.meta.env.VITE_API_BASE_URL}/assistanceRequests`
        : `${import.meta.env.VITE_API_BASE_URL}/assistanceRequests`; // Non includiamo l'ID nell'URL per la PATCH
      
      console.log(`Eseguo ${isCreatingNew ? 'POST' : 'PATCH'} a ${apiUrl}`);
      console.log('Corpo della richiesta:', { assistanceRequest });
      
      const response = await fetch(apiUrl, {
        method: isCreatingNew ? 'POST' : 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        },
        body: JSON.stringify({ assistanceRequest }) // Incapsulare assistanceRequest in un oggetto
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Errore dal server:', errorData);
        throw new Error(errorData.message || 'Errore durante la creazione della richiesta');
      }
      
      const responseData = await response.json();
      console.log('Risposta API:', JSON.stringify(responseData, null, 2));
      
      // Mostra un messaggio diverso in base all'operazione eseguita
      if (isCreatingNew) {
        // Se è abilitata la teleassistenza, devo creare un pagamento e reindirizzare
        if (assistanceRequest.teleassistenza && formData.tipoAssistenza) {
          console.log('Teleassistenza abilitata, verifico se è necessario un pagamento prepagato');
          
          // Cerco l'elemento con prepaid=true nel tipo di assistenza (struttura annidata)
          let paymentAmount = null;
          
          // Debug per verificare la struttura
          console.log('Struttura del tipo assistenza:', JSON.stringify(formData.tipoAssistenza, null, 2));
          
          // La struttura è tipoAssistenza.items[].elements[]
          if (formData.tipoAssistenza.items && Array.isArray(formData.tipoAssistenza.items)) {
            // Itero sugli items
            for (const item of formData.tipoAssistenza.items) {
              if (item.elements && Array.isArray(item.elements)) {
                // Cerco un elemento con prepaid=true
                const prepaidElement = item.elements.find(element => element.prepaid === true);
                if (prepaidElement && prepaidElement.value) {
                  paymentAmount = prepaidElement.value;
                  console.log(`Trovato elemento prepagato con valore: ${paymentAmount} in ${item.title}`);
                  break; // Prendo il primo elemento prepagato che trovo
                }
              }
            }
          }
          
          if (paymentAmount) {
            try {
              // Log dettagliato dell'utente per debug
              console.log('Dati utente completi:', user);
              console.log('ID utente:', user?._id);
              
              // Recuperiamo il customerUserId direttamente dal localStorage
              const userData = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
              const customerUserId = userData?.customerUserId; // Questo è il valore corretto da usare
              console.log('customerUserId da localStorage:', customerUserId);
              
              // Generiamo un ID temporaneo lato client per il pagamento
              const clientGeneratedId = `temp-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
              
              // Creo un nuovo pagamento
              const paymentData = {
                accountId: accountData._id,
                customerId: formData.cliente.id,
                customerUserId: customerUserId || '', // Usiamo direttamente il customerUserId dal localStorage
                amount: paymentAmount,
                paymentMethod: 'card', // Default a carta di credito
                status: 'pending',
                description: `Pagamento per teleassistenza: ${formData.tipoAssistenza.title || formData.tipoAssistenza.nome}`,
                referenceType: 'assistance_request',
                clientId: clientGeneratedId, // ID generato lato client
                // Verifichiamo la struttura della risposta per accedere all'ID correttamente
                assistanceRequestId: responseData._id || 
                                   (responseData.data && responseData.data._id) || 
                                   (responseData.assistanceRequest && responseData.assistanceRequest._id) || 
                                   id // Usiamo l'ID esistente come fallback
              };
              
              // Log completo dell'oggetto pagamento
              console.log('Oggetto pagamento completo:', paymentData);
              
              console.log('Creo un nuovo pagamento:', paymentData);
              
              // Effettuo la chiamata API per creare il pagamento
              // Il backend si aspetta un oggetto { payment: {...} }
              console.log('Invio richiesta di creazione pagamento con body:', { payment: paymentData });
              
              const paymentResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/customerPayments`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'auth-token': token
                },
                body: JSON.stringify({ payment: paymentData })
              });
              
              if (!paymentResponse.ok) {
                throw new Error('Errore durante la creazione del pagamento');
              }
              
              const paymentResult = await paymentResponse.json();
              console.log('Pagamento creato con successo:', JSON.stringify(paymentResult, null, 2));
              
              // Otteniamo l'ID del pagamento dalla risposta o usiamo l'ID generato lato client come fallback
              const paymentId = 
                (paymentResult && paymentResult._id) ||
                (paymentResult && paymentResult.data && paymentResult.data._id) ||
                (paymentResult && paymentResult.payment && paymentResult.payment._id) ||
                clientGeneratedId; // Fallback all'ID temporaneo generato lato client
              
              // Log per debug
              console.log(`Pagamento creato con ID ${paymentId}`);
              
              // Mostro un toast di informazione
              toast.success('Pagamento creato con successo, reindirizzamento alla pagina di pagamento...');
              
              // Reindirizzo direttamente l'utente alla pagina di pagamento
              navigate(`/customerPaymentPage?amount=${paymentAmount}&paymentId=${paymentId}`);
              return; // Interrompiamo il flusso qui
            } catch (paymentError) {
              console.error('Errore durante la creazione del pagamento:', paymentError);
              toast.error('Richiesta creata ma si è verificato un errore con il pagamento');
            }
          } else {
            console.log('Nessun elemento prepagato trovato nel tipo di assistenza');
            // Mostra modale di successo senza pagamento
            setShowSuccessModal(true);
          }
        } else {
          // Nessuna teleassistenza, mostra modale di successo
          setShowSuccessModal(true);
        }
      } else {
        toast.success('Richiesta di assistenza aggiornata con successo');
        setTimeout(() => navigate('/customerDashboard'), 1500);
      }
    } catch (error) {
      console.error('Errore durante la creazione della richiesta:', error);
      toast.error('Si è verificato un errore durante la creazione della richiesta');
    } finally {
      setIsSubmitting(false); // Disattiva lo stato di caricamento indipendentemente dal risultato
    }
  };

  const renderStep = () => {
    // Mostra indicatore di caricamento mentre recuperiamo i dati del cliente
    if (loadingCustomer) {
      return (
        <div className="flex items-center justify-center p-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Caricamento dati cliente...</span>
        </div>
      );
    }
    
    // Con il nuovo array steps, abbiamo i seguenti indici:
    // 0: Scegli Impianto
    // 1: Segnalazione
    // 2: Immagini
    // 3: Tipo Assistenza
    // 4: Pagamento
    
    switch (currentStep) {
      case 0: // Scegli Impianto (primo step)
        return (
          <ScegliImpiantoStep
            formData={formData}
            nextStep={handleNext}
            prevStep={null}
            setFormData={setFormData}
            isMobile={isMobile}
          />
        );
      case 1: // Segnalazione (secondo step)
        return (
          <SegnalazioneStep
            formData={formData}
            nextStep={handleNext}
            prevStep={handlePrev}
            setFormData={setFormData}
            isMobile={isMobile}
          />
        );
      case 2: // Immagini (terzo step)
        return (
          <ImmaginiStep
            formData={formData}
            nextStep={handleNext}
            prevStep={handlePrev}
            setFormData={setFormData}
            isMobile={isMobile}
          />
        );
      case 3: // Tipo Assistenza (quarto step ora)
        return (
          <TipoAssistenzaStep
            formData={formData}
            nextStep={handleNext}
            prevStep={handlePrev}
            setFormData={setFormData}
            isMobile={isMobile}
          />
        );
      case 4: // Pagamento (quinto step ora)
        return (
          <PagamentoStep
            formData={formData}
            prevStep={handlePrev}
            nextStep={handleNext}
            setFormData={setFormData}
            confirmSubmit={submitForm}
            isSubmitting={isSubmitting}
            isEditMode={isEditMode}
            isMobile={isMobile}
          />
        );
      default:
        // Se per qualche motivo il currentStep non corrisponde a nessuno dei casi sopra,
        // fallback al primo step (Scegli Impianto)
        return (
          <ScegliImpiantoStep
            formData={formData}
            nextStep={handleNext}
            prevStep={null}
            setFormData={setFormData}
            isMobile={isMobile}
          />
        );
    }
  };

  // Versione Mobile
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16">
        {/* Header Mobile Fisso */}
        <div className="bg-white shadow-sm px-3 py-2.5 sticky top-0 z-10">
          <h1 className="text-base font-bold mb-1.5">
            {id ? 'Modifica Assistenza' : 'Nuova Assistenza'}
          </h1>
          
          {/* Progress Bar Mobile */}
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-600">
              Step {currentStep + 1} di {steps.length}
            </span>
            <span className="text-xs font-medium text-blue-600">
              {steps[currentStep].title}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Contenuto Step Mobile */}
        <div className="p-2">
          <div className={`bg-white rounded-lg shadow p-3 min-h-[65vh] ${currentStep === steps.length - 1 ? 'mb-16' : ''}`}>
            {renderStep()}
          </div>
        </div>

        {/* Navigation Footer Mobile Fisso - Nascosto sull'ultimo step */}
        {currentStep !== steps.length - 1 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-3 py-2.5 flex justify-between items-center shadow-lg">
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentStep === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <i className="fi fi-br-angle-left text-xs"></i>
            <span>Indietro</span>
          </button>

          <div className="flex gap-1.5">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  index === currentStep
                    ? 'bg-blue-600 w-3'
                    : index < currentStep
                    ? 'bg-blue-400'
                    : 'bg-gray-300'
                }`}
              ></div>
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={currentStep === steps.length - 1}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentStep === steps.length - 1
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <span>Avanti</span>
            <i className="fi fi-br-angle-right text-xs"></i>
          </button>
        </div>
        )}

        {/* Modale di Successo Mobile */}
        {showSuccessModal && (
          <>
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes scaleIn {
                from { transform: scale(0.8); opacity: 0; }
                to { transform: scale(1); opacity: 1; }
              }
              .animate-fade-in {
                animation: fadeIn 0.3s ease-out;
              }
              .animate-scale-in {
                animation: scaleIn 0.5s ease-out;
              }
            `}</style>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" style={{ animation: 'scaleIn 0.4s ease-out' }}>
                {/* Icona di successo */}
                <div className="pt-6 pb-3 flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
                    <i className="fa-solid fa-check text-3xl text-green-600"></i>
                  </div>
                </div>

                {/* Contenuto */}
                <div className="px-6 pb-6 text-center">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    Richiesta Inviata!
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    La tua richiesta di assistenza è stata creata correttamente. 
                    Il nostro team ti contatterà al più presto.
                  </p>

                  {/* Pulsante */}
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      navigate('/customerDashboard');
                    }}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg"
                  >
                    Torna alla Dashboard
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Versione Desktop (layout originale)
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          {id ? 'Modifica Richiesta di Assistenza' : 'Nuova Richiesta di Assistenza'}
        </h1>
        
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <StepIcon
                icon={step.icon}
                isActive={currentStep === index}
                isCompleted={currentStep > index}
              />
              <span className={`ml-2 ${currentStep === index ? 'font-medium text-blue-600' : 'text-gray-600'}`}>
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <div className="w-16 h-0.5 bg-gray-200 mx-4"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-lg shadow">
          {renderStep()}
        </div>
        
        <div className="lg:col-span-1 sticky top-4" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
          <div className="bg-white rounded-lg shadow p-3 overflow-y-auto" style={{ maxHeight: '100%' }}>
            <h2 className="text-xl font-semibold mb-4 flex items-center text-blue-600">
              <i className="fa-solid fa-clipboard-list mr-2"></i>
              Riepilogo Richiesta
            </h2>
            <AssistancePreview
              formData={formData}
              currentStep={currentStep}
              submitForm={submitForm}
            />
          </div>
        </div>
      </div>

      {/* Modale di Successo */}
      {showSuccessModal && (
        <>
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes scaleIn {
              from { transform: scale(0.8); opacity: 0; }
              to { transform: scale(1); opacity: 1; }
            }
            .animate-fade-in {
              animation: fadeIn 0.3s ease-out;
            }
            .animate-scale-in {
              animation: scaleIn 0.5s ease-out;
            }
          `}</style>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" style={{ animation: 'scaleIn 0.4s ease-out' }}>
              {/* Icona di successo */}
              <div className="pt-8 pb-4 flex justify-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
                  <i className="fa-solid fa-check text-4xl text-green-600"></i>
                </div>
              </div>

              {/* Contenuto */}
              <div className="px-8 pb-8 text-center">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  Richiesta Inviata con Successo!
                </h3>
                <p className="text-gray-600 mb-6">
                  La tua richiesta di assistenza è stata creata correttamente. 
                  Il nostro team ti contatterà al più presto.
                </p>

                {/* Pulsante */}
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/customerDashboard');
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Torna alla Dashboard
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerAddEditAssistanceRequest; 